/* Service for managing post-quiz & post-exam difficulty ratings */

export interface DifficultyRatingInfo {
  avgRating: number;      // e.g. 4.1
  count: number;          // e.g. 52
  userRating?: number;    // 1 to 5 if current user has rated
}

export type DifficultyLevel = 'Very Easy' | 'Easy' | 'Medium' | 'Hard' | 'Very Hard';

export interface DifficultyMeta {
  level: DifficultyLevel;
  label: string;
  badgeClass: string;
  badgeBg: string;
  textClass: string;
  borderClass: string;
  starColor: string;
  description: string;
}

const STORAGE_KEY = 'cna_quiz_difficulty_ratings_v2';
const API_BASE_URL = '/practice-exams/api/ratings';

// Initial baseline community ratings pre-seeded for realistic student feedback
const PRESEEDED_RATINGS: Record<string, { totalScore: number; count: number }> = {
  // Exam Vendors & Modes
  'exam-Prometric': { totalScore: 187, count: 48 },        // ~3.9
  'exam-Headmaster': { totalScore: 254, count: 62 },       // ~4.1
  'exam-NNAAP': { totalScore: 216, count: 54 },            // ~4.0
  'exam-Pearson VUE': { totalScore: 298, count: 71 },      // ~4.2
  'exam-standard': { totalScore: 329, count: 89 },         // ~3.7
  'exam-practice': { totalScore: 347, count: 102 },        // ~3.4
  'exam-scenario': { totalScore: 163, count: 38 },         // ~4.3
  'exam-scenario_quiz': { totalScore: 172, count: 41 },    // ~4.2
  'exam-advanced': { totalScore: 126, count: 28 },         // ~4.5

  // Quiz Topics
  'quiz-Mixed': { totalScore: 456, count: 120 },           // ~3.8
  'quiz-Basic Nursing Skills': { totalScore: 148, count: 45 }, // ~3.3
  'quiz-Personal Care Skills': { totalScore: 118, count: 38 }, // ~3.1
  'quiz-Mental Health & Social Care Needs': { totalScore: 104, count: 29 }, // ~3.6
  'quiz-Rights & Legal Responsibilities': { totalScore: 199, count: 51 }, // ~3.9
  'quiz-Communication & Care Team Interactions': { totalScore: 96, count: 32 }, // ~3.0
  'quiz-Safety & Emergency Procedures': { totalScore: 275, count: 67 }, // ~4.1
  'quiz-Infection Control & Bio-Safety': { totalScore: 336, count: 80 }, // ~4.2
  'quiz-Missed Questions': { totalScore: 418, count: 95 }, // ~4.4
};

// Normalize key aliases (e.g., 'Prometric' -> 'exam-Prometric')
export const normalizeItemId = (rawId: string, isExam: boolean = false): string => {
  if (!rawId) return isExam ? 'exam-standard' : 'quiz-Mixed';
  const clean = rawId.trim();
  if (clean.startsWith('exam-') || clean.startsWith('quiz-')) return clean;
  if (isExam) return `exam-${clean}`;
  return `quiz-${clean}`;
};

// Local storage helper
interface LocalState {
  ratings: Record<string, { totalScore: number; count: number }>;
  userRatings: Record<string, number>;
}

const getLocalState = (): LocalState => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      return {
        ratings: parsed.ratings || {},
        userRatings: parsed.userRatings || {},
      };
    }
  } catch (e) {
    console.warn('Failed to read difficulty ratings from localStorage:', e);
  }
  return { ratings: {}, userRatings: {} };
};

const saveLocalState = (state: LocalState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent('difficulty-ratings-updated', { detail: state }));
  } catch (e) {
    console.warn('Failed to save difficulty ratings to localStorage:', e);
  }
};

export const DifficultyRatingService = {
  /**
   * Get difficulty ratings for a given item ID (exam or quiz topic)
   */
  getRating(itemId: string, isExam: boolean = false): DifficultyRatingInfo {
    const cleanId = normalizeItemId(itemId, isExam);
    const localState = getLocalState();

    // Check pre-seeded default
    const preseeded = PRESEEDED_RATINGS[cleanId] || PRESEEDED_RATINGS[itemId] || { totalScore: 19, count: 5 }; // default ~3.8

    // Combine preseeded baseline with user local additions
    const custom = localState.ratings[cleanId] || { totalScore: 0, count: 0 };
    const totalScore = preseeded.totalScore + custom.totalScore;
    const count = preseeded.count + custom.count;

    const avgRating = count > 0 ? Math.round((totalScore / count) * 10) / 10 : 3.8;
    const userRating = localState.userRatings[cleanId];

    return {
      avgRating,
      count,
      userRating,
    };
  },

  /**
   * Submit a student difficulty rating (1 to 5)
   */
  async submitRating(itemId: string, rating: number, isExam: boolean = false, userId: string = 'guest'): Promise<DifficultyRatingInfo> {
    const cleanId = normalizeItemId(itemId, isExam);
    const clampedRating = Math.max(1, Math.min(5, Math.round(rating)));

    const localState = getLocalState();
    const prevUserRating = localState.userRatings[cleanId];

    let custom = localState.ratings[cleanId] || { totalScore: 0, count: 0 };

    if (prevUserRating !== undefined) {
      // User is updating their previous rating
      custom.totalScore = custom.totalScore - prevUserRating + clampedRating;
    } else {
      // New user rating
      custom.totalScore += clampedRating;
      custom.count += 1;
    }

    localState.ratings[cleanId] = custom;
    localState.userRatings[cleanId] = clampedRating;
    saveLocalState(localState);

    // Sync to backend asynchronously if available
    try {
      fetch(`${API_BASE_URL}/${encodeURIComponent(cleanId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: clampedRating, userId }),
      }).catch(() => {});
    } catch (err) {
      // Ignore API errors, localStorage keeps state
    }

    return this.getRating(cleanId, isExam);
  },

  /**
   * Helper to map average rating score to visual badges and levels
   */
  getMeta(avgRating: number): DifficultyMeta {
    if (avgRating <= 2.2) {
      return {
        level: 'Very Easy',
        label: 'Very Easy',
        badgeClass: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60',
        badgeBg: 'bg-emerald-500/10',
        textClass: 'text-emerald-600 dark:text-emerald-400',
        borderClass: 'border-emerald-200 dark:border-emerald-800',
        starColor: 'text-emerald-500',
        description: 'Direct recall questions & core nursing concepts',
      };
    }
    if (avgRating <= 3.2) {
      return {
        level: 'Easy',
        label: 'Easy',
        badgeClass: 'bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300 border-teal-200 dark:border-teal-800/60',
        badgeBg: 'bg-teal-500/10',
        textClass: 'text-teal-600 dark:text-teal-400',
        borderClass: 'border-teal-200 dark:border-teal-800',
        starColor: 'text-teal-500',
        description: 'Standard clinical scenarios & ADL procedures',
      };
    }
    if (avgRating <= 3.8) {
      return {
        level: 'Medium',
        label: 'Medium',
        badgeClass: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border-amber-200 dark:border-amber-800/60',
        badgeBg: 'bg-amber-500/10',
        textClass: 'text-amber-600 dark:text-amber-400',
        borderClass: 'border-amber-200 dark:border-amber-800',
        starColor: 'text-amber-500',
        description: 'Balanced mix of recall & state board safety logic',
      };
    }
    if (avgRating <= 4.3) {
      return {
        level: 'Hard',
        label: 'Hard',
        badgeClass: 'bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300 border-orange-200 dark:border-orange-800/60',
        badgeBg: 'bg-orange-500/10',
        textClass: 'text-orange-600 dark:text-orange-400',
        borderClass: 'border-orange-200 dark:border-orange-800',
        starColor: 'text-orange-500',
        description: 'Tricky priority decisions & state safety rules',
      };
    }
    return {
      level: 'Very Hard',
      label: 'Very Hard',
      badgeClass: 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 border-rose-200 dark:border-rose-800/60',
      badgeBg: 'bg-rose-500/10',
      textClass: 'text-rose-600 dark:text-rose-400',
      borderClass: 'border-rose-200 dark:border-rose-800',
      starColor: 'text-rose-500',
      description: 'Complex multi-step clinical cases & error traps',
    };
  },
};
